import { cn } from "~/lib/utils";
import type { CmmdTaskStatus } from "@cmmd-forge/contracts";
import { Trash2Icon } from "lucide-react";
import { WorkItemProjectIcon } from "./WorkItemProjectIcon";
import type { WorkItem } from "../homebase/unifiedWorkItems";
import { formatDueDate, formatTaskDisplayId } from "./displayHelpers";
import { AgentBadge } from "./TaskRow";
import { STATUS_CONFIGS } from "./statusConfig";

interface TaskCardProps {
  item: WorkItem;
  isSelected: boolean;
  onSelect: (id: string) => void;
  onUpdateStatus?: (id: string, status: CmmdTaskStatus) => void;
  onDeleteTask?: (id: string) => void;
  columnStatus?: CmmdTaskStatus;
}

function avatarInitial(provider: string | null): string {
  if (!provider) return "A";
  return provider.charAt(0).toUpperCase();
}

function sourceLabel(item: WorkItem): string | null {
  if (item.source === "cmmd-campaign") return "Campaign";
  if (item.source === "cmmd-project") return "Project";
  return item.workspaceName;
}

export function TaskCard({
  item,
  isSelected,
  onSelect,
  onUpdateStatus,
  onDeleteTask,
  columnStatus,
}: TaskCardProps) {
  const isAgentActive = item.agentStatus === "running";
  const displayId = formatTaskDisplayId(item.id);
  const formattedDue = formatDueDate(item.dueDate);
  const canUpdateStatus = item.id.startsWith("manual:");
  const canDelete = item.id.startsWith("manual:") && onDeleteTask;
  return (
    <div
      role="button"
      tabIndex={0}
      onClick={() => onSelect(item.id)}
      onKeyDown={(event) => {
        if (event.key === "Enter" || event.key === " ") {
          event.preventDefault();
          onSelect(item.id);
        }
      }}
      draggable={canUpdateStatus}
      onDragStart={(event) => {
        if (!canUpdateStatus) {
          event.preventDefault();
          return;
        }
        event.dataTransfer.setData("text/command-center-task-id", item.id);
        event.dataTransfer.effectAllowed = "move";
      }}
      className={cn(
        "group w-full text-left rounded-lg border bg-card/80 p-3 transition-colors",
        canUpdateStatus ? "cursor-grab active:cursor-grabbing" : "cursor-pointer",
        "hover:border-border hover:bg-card",
        isSelected ? "border-primary/50" : "border-border",
        isAgentActive && "border-l-2 border-l-green-500 pl-[11px]",
      )}
    >
      <div className="mb-2 flex items-start justify-between gap-2">
        <span
          className={cn(
            "text-[15px] leading-snug font-medium",
            item.status === "done" ? "text-muted-foreground/40 line-through" : "text-foreground",
          )}
        >
          {item.title}
        </span>
        <span className="mt-0.5 shrink-0 font-mono text-xs text-muted-foreground/50 tabular-nums">
          {displayId}
        </span>
        {canDelete ? (
          <button
            type="button"
            aria-label={`Delete ${item.title}`}
            title="Delete local task"
            className="mt-0.5 inline-flex size-5 shrink-0 items-center justify-center rounded-sm text-muted-foreground/45 opacity-0 transition-colors hover:bg-destructive-surface hover:text-destructive group-hover:opacity-100 focus-visible:opacity-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            onClick={(event) => {
              event.stopPropagation();
              onDeleteTask(item.id);
            }}
          >
            <Trash2Icon className="size-3.5" />
          </button>
        ) : null}
      </div>
      <div className="mb-2 flex flex-wrap items-center gap-2">
        {sourceLabel(item) && (
          <span className="rounded border border-border px-2 py-0.5 text-xs font-medium text-muted-foreground/75">
            {sourceLabel(item)}
          </span>
        )}
        {/* Same identity ladder as the sidebar and the palette: a card that
            names a project without its mark is the odd one out. */}
        <span className="inline-flex min-w-0 items-center gap-1.5 rounded border border-border px-2 py-0.5 text-xs text-muted-foreground/70">
          <WorkItemProjectIcon
            projectName={item.projectName}
            projectIcon={item.projectIcon}
            projectIconUrl={item.projectIconUrl}
          />
          <span className="truncate">{item.projectName}</span>
        </span>
        {isAgentActive && (
          <AgentBadge agentStatus={item.agentStatus} agentProvider={item.agentProvider} />
        )}
        {canUpdateStatus && onUpdateStatus && (item.status ?? "todo") !== columnStatus && (
          <label
            className="ml-auto inline-flex items-center rounded border border-border px-2 py-0.5 text-xs text-muted-foreground/80"
            onClick={(event) => event.stopPropagation()}
          >
            <span className="mr-1">Status</span>
            <select
              value={typeof item.status === "string" ? item.status : "todo"}
              onChange={(event) =>
                onUpdateStatus(item.id, event.currentTarget.value as CmmdTaskStatus)
              }
              className="bg-transparent text-xs text-foreground outline-none"
              aria-label={`Update status for ${item.title}`}
            >
              {STATUS_CONFIGS.map((statusConfig) => (
                <option key={statusConfig.status} value={statusConfig.status}>
                  {statusConfig.label}
                </option>
              ))}
            </select>
          </label>
        )}
      </div>
      <div className="flex items-center justify-between gap-2">
        <span
          aria-hidden
          className="inline-flex size-6 items-center justify-center rounded-sm bg-muted text-xs font-semibold text-muted-foreground"
        >
          {avatarInitial(item.agentProvider)}
        </span>
        <span
          className={cn(
            "font-mono text-xs tabular-nums",
            formattedDue?.warn ? "text-warning-foreground" : "text-muted-foreground/40",
            formattedDue?.overdue && "font-semibold",
          )}
        >
          {formattedDue?.label ?? "No date"}
        </span>
      </div>
    </div>
  );
}
